import { useCallback, useEffect, useRef, useState } from "react";
import api, { MUTATION_EVENT } from "@/lib/api";

// Stale-while-revalidate cache for GET endpoints, scoped to the signed-in token.
// Any successful write (POST/PATCH/PUT/DELETE) wipes it and refetches mounted views.
const LS_PREFIX = "lxn_swr:";
const mem = new Map();
const listeners = new Set();
let generation = 0;

const scope = () => (localStorage.getItem("lxn_token") || "").slice(-24);
const keyOf = (url) => `${scope()}|${url}`;

export function readCache(url) {
  if (!url) return undefined;
  const key = keyOf(url);
  if (mem.has(key)) return mem.get(key);
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (raw) { const v = JSON.parse(raw); mem.set(key, v); return v; }
  } catch {}
  return undefined;
}

function writeCache(url, data) {
  const key = keyOf(url);
  mem.set(key, data);
  try { localStorage.setItem(LS_PREFIX + key, JSON.stringify(data)); } catch {}
}

export function clearApiCache() {
  generation += 1;
  mem.clear();
  try { Object.keys(localStorage).filter((k) => k.startsWith(LS_PREFIX)).forEach((k) => localStorage.removeItem(k)); } catch {}
}

if (typeof window !== "undefined") {
  window.addEventListener(MUTATION_EVENT, () => { clearApiCache(); listeners.forEach((fn) => fn()); });
}

export function useApi(url) {
  const [data, setData] = useState(() => readCache(url));
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(true);
  const reqId = useRef(0);

  const load = useCallback(async () => {
    if (!url) return;
    const my = ++reqId.current;
    const gen = generation;
    setFetching(true); setError(null);
    try {
      const res = await api.get(url, { timeout: 20000 });
      if (my !== reqId.current) return;
      if (gen !== generation) { load(); return; }
      writeCache(url, res.data);
      setData(res.data);
    } catch (e) {
      if (my === reqId.current) setError(e);
    } finally {
      if (my === reqId.current) setFetching(false);
    }
  }, [url]);

  useEffect(() => { setData(readCache(url)); load(); }, [url, load]);
  useEffect(() => { listeners.add(load); return () => { listeners.delete(load); }; }, [load]);

  return { data, setData, error: data === undefined ? error : null, loading: data === undefined && fetching && !error, reload: load };
}

// Renders long lists in batches; filtering/search still runs on the full list.
export function useIncremental(items, step = 40) {
  const [count, setCount] = useState(step);
  const sentinelRef = useRef(null);
  const hasMore = count < items.length;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) setCount((c) => c + step); }, { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, step, count]);
  return { visible: items.slice(0, count), sentinelRef, hasMore };
}
